import React, {useState} from 'react'
import {Button} from 'react-daisyui'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faCheckCircle, faClose} from '@fortawesome/free-solid-svg-icons'
import {
  useApplyModifyReservationMutation,
  useQuoteModifyReservationMutation
} from '../../services/guestPortalApi'
import DatePickerInput from '../common/DatePickerInput'
import LoadingSpinner, {LoadingSpinnerSize} from '../common/LoadingSpinner'
import {
  trackBookingManagement,
  trackFormSubmission
} from '../../services/analytics'
import {trackClick} from '../../services/coreAnalytics'
import {getUser, getUserToken} from '../../services/token'
import {formatDateString} from '../../services/utils'
import {
  applyErrorMessageFor,
  confirmationTotal,
  formatMoney,
  ineligibleMessageFor,
  ModifyQuoteResponse,
  toIsoDate,
  toLocalDate
} from './modifyReservationHelpers'
import ChangeRecap, {ApplyErrorNotice} from './ChangeRecap'

interface ModifyReservationFormProps {
  reservation: any
  onDone?: () => void
  onGoBack?: () => void
}

const userEmail = (): string | undefined => {
  const token = getUserToken()
  return token ? getUser(token)?.email : undefined
}

const ModifyReservationForm: React.FC<ModifyReservationFormProps> = ({
  reservation,
  onDone,
  onGoBack
}) => {
  const [quoteModify, quoteResult] = useQuoteModifyReservationMutation()
  const [applyModify, applyResult] = useApplyModifyReservationMutation()

  const [arrival, setArrival] = useState<Date | undefined>(() =>
    toLocalDate(reservation.checkinDate)
  )
  const [departure, setDeparture] = useState<Date | undefined>(() =>
    toLocalDate(reservation.checkoutDate)
  )
  const [applyError, setApplyError] = useState<ApplyErrorNotice | null>(null)
  const [showSuccess, setShowSuccess] = useState(false)
  const [step, setStep] = useState<'dates' | 'recap'>('dates')
  // Snapshot of the eligible quote the recap displays — kept in state so
  // the recap survives while a refreshed quote is in flight, and so the
  // guest always confirms exactly the totals they were shown
  const [recapQuote, setRecapQuote] = useState<ModifyQuoteResponse | null>(null)

  const quote: ModifyQuoteResponse | undefined = quoteResult.data
  const analyticsPage = `/member/stays/${reservation.reservationId}`

  const showRecap = (eligibleQuote: ModifyQuoteResponse) => {
    // TEMP debug (2026-09-03) — remove before commit: verifying taxesAndFees
    // survives the trip from the quote API into the recap
    // eslint-disable-next-line no-console
    console.log('TEMP modify-tax debug — recap quote', {
      taxes: eligibleQuote.taxes,
      fees: eligibleQuote.fees,
      grandTotal: eligibleQuote.grandTotal,
      taxesAndFeesScope: eligibleQuote.detail?.taxesAndFeesScope,
      kind: eligibleQuote.kind,
      pricingPath: eligibleQuote.pricingPath,
      detail: eligibleQuote.detail
    })
    setRecapQuote(eligibleQuote)
    setStep('recap')
    trackBookingManagement(
      'modify_recap_viewed',
      reservation.reservationId,
      undefined,
      userEmail()
    )
  }

  const backToDates = () => {
    setStep('dates')
    setRecapQuote(null)
    setApplyError(null)
  }

  const requestQuote = () => {
    if (!arrival || !departure) {
      return
    }
    const email = userEmail()
    trackClick('get-modify-quote-button', analyticsPage, email)
    trackFormSubmission('booking_modify', 'form_submitted', undefined, email)
    setApplyError(null)
    quoteModify({
      reservationId: reservation.reservationId,
      arrival: toIsoDate(arrival),
      departure: toIsoDate(departure)
    })
      .unwrap()
      .then(data => {
        if (data.eligible) {
          showRecap(data)
        }
      })
      // Quote failures stay on the dates step, where quoteResult.isError
      // already renders the error message
      .catch(() => backToDates())
  }

  const confirmChange = () => {
    if (!recapQuote?.eligible || !arrival || !departure) {
      return
    }
    const email = userEmail()
    trackClick('confirm-modify-reservation-button', analyticsPage, email)
    applyModify({
      reservationId: reservation.reservationId,
      arrival: toIsoDate(arrival),
      departure: toIsoDate(departure),
      expectedNewTotal: confirmationTotal(recapQuote)
    })
      .unwrap()
      .then(() => {
        trackBookingManagement(
          'modify_booking_completed',
          reservation.reservationId,
          undefined,
          email
        )
        trackFormSubmission('booking_modify', 'form_success', undefined, email)
        setShowSuccess(true)
      })
      .catch(error => {
        trackBookingManagement(
          'modify_booking_failed',
          reservation.reservationId,
          'API Error',
          email
        )
        trackFormSubmission(
          'booking_modify',
          'form_error',
          ['Unable to modify reservation'],
          email
        )
        const quoteChanged = error?.data?.error === 'quote-changed'
        setApplyError({
          message: applyErrorMessageFor(error),
          tone: quoteChanged ? 'warning' : 'error'
        })
        // Rates moved between preview and confirm — refresh the quote so
        // the guest confirms against the current price. The recap stays up
        // and re-renders with the new totals unless the change is no
        // longer available
        if (quoteChanged) {
          quoteModify({
            reservationId: reservation.reservationId,
            arrival: toIsoDate(arrival),
            departure: toIsoDate(departure)
          })
            .unwrap()
            .then(data => {
              if (data.eligible) {
                setRecapQuote(data)
              } else {
                setStep('dates')
                setRecapQuote(null)
              }
            })
            .catch(() => {
              setStep('dates')
              setRecapQuote(null)
            })
        }
      })
  }

  const isBusy = quoteResult.isLoading || applyResult.isLoading

  if (showSuccess) {
    const outcome = applyResult.data?.outcome
    return (
      <div>
        <div className="absolute top-0 right-0">
          <button
            className="btn-close"
            aria-label="Close date change confirmation"
            onClick={() => onDone?.()}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') {
                if (onDone) {
                  onDone()
                }
              }
            }}
          >
            <FontAwesomeIcon
              icon={faClose}
              className="text-xl fa-thin text-center"
            />
          </button>
        </div>
        <div className="text-green-500 flex flex-col items-center p-4">
          <FontAwesomeIcon icon={faCheckCircle} className="text-5xl p-4" />
        </div>
        <div className="text-center flex flex-col gap-2">
          <p className="font-semibold">Done. Your dates are updated.</p>
          {outcome?.adjustmentPending && (
            <p>
              Your confirmed total is{' '}
              {formatMoney(confirmationTotal(recapQuote))}. Our team is
              finishing the folio update — it’ll reflect shortly.
            </p>
          )}
        </div>
      </div>
    )
  }

  if (step === 'recap' && recapQuote && arrival && departure) {
    return (
      <div
        aria-labelledby="modify-reservation-title"
        onKeyDown={e => e.key === 'Escape' && backToDates()}
      >
        <ChangeRecap
          currentCheckin={reservation.checkinDate}
          currentCheckout={reservation.checkoutDate}
          arrival={arrival}
          departure={departure}
          quote={recapQuote}
          applyError={applyError}
          isBusy={isBusy}
          onBackToDates={backToDates}
          onConfirm={confirmChange}
        />
      </div>
    )
  }

  return (
    <div
      aria-labelledby="modify-reservation-title"
      onKeyDown={e => e.key === 'Escape' && onGoBack?.()}
    >
      <h1
        id="modify-reservation-title"
        className="font-bold text-2xl mb-2"
        role="heading"
        aria-level={1}
      >
        Change Your Dates
      </h1>
      <p className="mb-4">
        Currently {formatDateString(reservation.checkinDate)} –{' '}
        {formatDateString(reservation.checkoutDate)}. Pick your new dates and
        we’ll show the updated price before anything changes.
      </p>

      <div className="flex flex-col md:flex-row gap-4 mb-4">
        <div className="flex-1">
          <label className="block mb-1 font-medium">New check-in</label>
          <DatePickerInput
            value={arrival}
            onChange={setArrival}
            placeholder="MM/DD/YYYY"
          />
        </div>
        <div className="flex-1">
          <label className="block mb-1 font-medium">New check-out</label>
          <DatePickerInput
            value={departure}
            onChange={setDeparture}
            placeholder="MM/DD/YYYY"
          />
        </div>
      </div>

      {isBusy && (
        <div
          className="flex flex-row justify-center items-center py-4 gap-2"
          role="status"
          aria-live="polite"
        >
          <LoadingSpinner size={LoadingSpinnerSize.Large} />
          <span>Checking availability…</span>
        </div>
      )}

      {quoteResult.isError && !isBusy && (
        <div className="text-red-500 mb-4" role="alert" aria-live="assertive">
          We couldn’t price those dates. Try again or contact the property.
        </div>
      )}

      {quote && !quote.eligible && !isBusy && (
        <div className="bg-base-200 rounded-lg p-4 mb-4" role="alert">
          {ineligibleMessageFor(quote.reason)}
        </div>
      )}

      <div className="flex justify-end p-2 gap-2">
        <Button
          className="btn-md btn-outline btn-secondary"
          onClick={() => {
            trackFormSubmission(
              'booking_modify',
              'form_cancelled',
              undefined,
              userEmail()
            )
            if (onGoBack) {
              onGoBack()
            }
          }}
          aria-label="Go back without changing dates"
        >
          go back
        </Button>
        <Button
          className="btn btn-md btn-secondary"
          disabled={!arrival || !departure || isBusy}
          onClick={requestQuote}
          aria-label="See the updated price for the new dates"
        >
          see new price
        </Button>
      </div>
    </div>
  )
}

export default ModifyReservationForm
